/* "Goes with this" — ONE curated block at the bottom of the cart (Jamie, 2026-09-06).

   Was a panel under every line, which repeated the same suggestion three or four times and,
   worse, used Shopify's "related products" and offered a speaker kit next to a speaker kit.

   The pairings are curated in assets/pvs-upsell-map.json (built from our own combo kits: what
   a combo bundles together IS a validated pairing). This file only applies them:
     - classify every cart line to a category and a vehicle,
     - collect complement categories in the map's order,
     - ⛔ drop any category the cart ALREADY holds, and anything in `never_pair`,
     - drop products already in the cart, dedupe, show a handful.
   ⛔ Never fall back to Shopify recommendations here. That is the bug this replaced. */
(function () {
  const MAX = 4;
  const container = () => document.getElementById('pvs-goes-with');

  const money = (cents) => `$${(cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

  async function json(url) {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`${url} ${response.status}`);
    return response.json();
  }

  function classify(map, line) {
    const type = (line.product_type || '').toLowerCase();
    const title = (line.product_title || '').toLowerCase();
    const handles = (line.collections || []).map((c) => c.toLowerCase());

    let category = null;
    for (const [key, def] of Object.entries(map.categories)) {
      const m = def.match || {};
      const byType = (m.types || []).some((t) => t.toLowerCase() === type);
      const byColl = (m.collections || []).some((c) => handles.includes(c.toLowerCase()));
      const byTitle = (m.title_contains || []).some((t) => title.includes(t.toLowerCase()));
      if (byType || byColl || byTitle) { category = key; break; }
    }

    let vehicle = null;
    for (const key of map.vehicle_priority || Object.keys(map.vehicles)) {
      const def = map.vehicles[key];
      if (!def) continue;
      const byColl = (def.collections || []).some((c) => handles.includes(c.toLowerCase()));
      const byTag = (def.tags || []).some((t) => (line.tags || []).some((x) => x.toLowerCase() === t.toLowerCase()));
      const byTitle = (def.title_contains || []).some((t) => title.includes(t.toLowerCase()));
      if (byColl || byTag || byTitle) { vehicle = key; break; }
    }

    return { category, vehicle };
  }

  // The cart page renders the block with data-pending, which reserves its height while the
  // picks load so the lines under it do not jump (2026-09-29). Clear it on every exit path.
  function settle(el, show) {
    el.removeAttribute('data-pending');
    el.removeAttribute('aria-hidden');
    el.hidden = !show;
  }

  async function build() {
    const el = container();
    if (!el) return;

    const [map, cart] = await Promise.all([
      json(el.dataset.map),
      json(`${window.theme.routes.cart}.js`)
    ]);

    if (!cart.items.length) { settle(el, false); el.innerHTML = ''; return; }

    // Products need their collections to classify, and /cart.js does not carry them.
    const lines = await Promise.all(cart.items.map(async (item) => {
      try {
        const product = await json(`/products/${item.handle}.js`);
        return { ...item, product_type: product.type, tags: product.tags,
                 collections: (product.collections || []).map((c) => c.handle || c) };
      } catch (error) {
        return { ...item, product_type: item.product_type, tags: [], collections: [] };
      }
    }));

    const inCartHandles = new Set(lines.map((l) => l.handle));
    const classified = lines.map((l) => classify(map, l));
    const heldCategories = new Set(classified.map((c) => c.category).filter(Boolean));
    // A held category can already include a complement (a wheel upgrade kit ships with the wireless
    // module), so count that complement as held and never offer it. Data lives in map.bundled_with.
    classified.forEach(({ category }) => {
      ((map.bundled_with || {})[category] || []).forEach((included) => heldCategories.add(included));
    });

    // Vehicle: whatever the cart is mostly about, by the map's own priority order.
    const vehicle = (map.vehicle_priority || []).find((v) => classified.some((c) => c.vehicle === v)) || null;

    // never_pair is VEHICLE AWARE: a category listed under never_pair_by_vehicle[vehicle]
    // replaces its global block list for that vehicle. The global rules encode LandCruiser
    // product truths (headunit blocks reverse_camera because the MK4 ships with a camera) and
    // those are wrong on other platforms.
    const blocksFor = (category) => {
      const perVehicle = ((map.never_pair_by_vehicle || {})[vehicle] || {});
      const list = Object.prototype.hasOwnProperty.call(perVehicle, category)
        ? perVehicle[category]
        : ((map.never_pair || {})[category] || []);
      return new Set([category, ...list]);               // a category never pairs with itself
    };

    // ⛔ On a cross_promote_all platform the global `pairs` ladder is WRONG, not merely
    // incomplete. `pairs.headunit` is the LandCruiser audio ladder (door pods, speakers,
    // speaker wiring, subwoofer) and an R35 has none of it, so every one of those categories
    // was wanted, found no vehicle pick, and fell through to _universal -- which put an
    // MBQuart subwoofer next to a GT-R head unit (Jamie, 2026-09-22). These platforms are
    // described ENTIRELY by their own picks block below.
    const ownPicksOnly = (map.cross_promote_all || []).includes(vehicle);

    const wanted = [];
    if (!ownPicksOnly) {
      classified.forEach(({ category }) => {
        if (!category) return;
        const blocked = blocksFor(category);
        (map.pairs[category] || []).forEach((complement) => {
          if (blocked.has(complement)) return;
          if (heldCategories.has(complement)) return;      // already in the cart
          if (!wanted.includes(complement)) wanted.push(complement);
        });
      });
    }

    // Platforms whose whole range is mutually compatible (cross_promote_all): offer any other
    // category that has picks for this vehicle. `pairs` is category-level and global, so its
    // `headunit` entry is the LandCruiser audio ladder, which an R35 has none of.
    if ((map.cross_promote_all || []).includes(vehicle)) {
      Object.keys(((map.picks || {})[vehicle] || {})).forEach((complement) => {
        if (heldCategories.has(complement)) return;
        if (classified.some(({ category }) => category && blocksFor(category).has(complement))) return;
        if (!wanted.includes(complement)) wanted.push(complement);
      });
    }

    const picksFor = (category) => {
      const byVehicle = ((map.picks || {})[vehicle] || {})[category] || [];
      // Same reason: _universal is the LandCruiser-era fallback (MBQuart sub, AHD reverse
      // cameras). On an own-picks-only platform it is the other half of the same leak.
      if (ownPicksOnly) return byVehicle;
      const universal = ((map.picks || {})._universal || {})[category] || [];
      return [...byVehicle, ...universal];
    };

    const chosen = [];
    const add = async (handle) => {
      if (chosen.length >= MAX) return false;
      if (inCartHandles.has(handle) || chosen.some((c) => c.handle === handle)) return false;
      try {
        const product = await json(`/products/${handle}.js`);
        const variant = (product.variants || []).find((v) => v.available);
        if (!variant) return false;
        chosen.push({ handle, title: product.title, url: `/products/${handle}`,
                      image: product.featured_image, price: variant.price, variantId: variant.id,
                      multi: (product.variants || []).length > 1 });
        return true;
      } catch (error) {
        return false;                                   // handle no longer live
      }
    };

    // One pick per category first, so the block reads as a spread (pods, wheel, speakers)
    // rather than two of the same thing. Only then go back for seconds.
    for (const category of wanted) {
      if (chosen.length >= MAX) break;
      for (const handle of picksFor(category)) {
        if (await add(handle)) break;
      }
    }
    for (const category of wanted) {
      if (chosen.length >= MAX) break;
      for (const handle of picksFor(category)) {
        if (chosen.length >= MAX) break;
        await add(handle);
      }
    }

    if (!chosen.length) { settle(el, false); el.innerHTML = ''; return; }

    el.innerHTML = `
      <p class="pvs-upsell__title">Goes with this</p>
      ${chosen.map((p) => `
        <div class="pvs-upsell__row">
          <span class="pvs-upsell__tile">${p.image ? `<img src="${p.image}" alt="" loading="lazy">` : ''}</span>
          <span class="pvs-upsell__info"><a class="pvs-upsell__name" href="${p.url}">${p.title}</a></span>
          <span class="pvs-upsell__price">${money(p.price)}</span>
          ${p.multi
            ? `<a class="pvs-upsell__add" href="${p.url}">Choose options</a>`
            : `<button type="button" class="pvs-upsell__add js-upsell-add" data-variant-id="${p.variantId}">Add</button>`}
        </div>`).join('')}`;
    settle(el, true);
  }

  document.addEventListener('click', async (evt) => {
    const btn = evt.target.closest('.js-upsell-add');
    if (!btn) return;

    btn.disabled = true;
    btn.textContent = 'Adding';
    try {
      const response = await fetch(`${window.theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.variantId), quantity: 1 }] })
      });
      if (!response.ok) throw new Error(response.status);

      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.refresh === 'function') {
        cartItems.refresh();
        build();
      } else {
        window.location.assign(window.theme.routes.cart);
      }
    } catch (error) {
      btn.disabled = false;
      btn.textContent = 'Add';
      const errors = document.getElementById('cart-errors');
      if (errors) { errors.textContent = 'That item could not be added. It may have sold out.'; errors.hidden = false; }
      console.log(error); // eslint-disable-line
    }
  });

  const start = () => build().catch((error) => {
    const el = container();
    if (el && el.hasAttribute('data-pending')) settle(el, false);
    console.log(error); // eslint-disable-line
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
